/**
 * Liquiditätsvorschau: offene Forderungen der Agentur (an Marken und an Creator) nach Fälligkeit gegen fällige
 * Auszahlungen an Creator, je Kalenderwoche und je Währung getrennt (EUR und CHF werden nie zusammengezählt).
 * Überfälliges steht gesondert, ebenso Posten ohne Termin: Rechnungen, die noch nicht versendet sind, und Auszahlungen,
 * die erst mit der Zahlung der Marke fällig werden (Auszahlungsregel „nach Zahlung der Marke“).
 */
import { addDays, weekday } from "./periods.js";
import type { Cents, IsoDate } from "./types.js";

export interface LiquidityItem {
  id: string;
  /** Eingang (Forderung der Agentur) oder Ausgang (Auszahlung an einen Creator) */
  kind: "in" | "out";
  currency: string;
  amountCents: Cents;
  /** Fälligkeit; null = ohne Termin (siehe waiting) */
  due: IsoDate | null;
  /** Warum ohne Termin: noch nicht versendet bzw. wartet auf die Zahlung der Marke */
  waiting?: "not_sent" | "brand_payment" | null;
  label?: string;
}

export interface LiquidityBucket {
  inCents: Cents;
  outCents: Cents;
  items: string[];
}

export interface LiquidityWeek extends LiquidityBucket {
  /** Montag und Sonntag der Woche */
  start: IsoDate;
  end: IsoDate;
  /** Kalenderwoche nach ISO 8601 */
  week: number;
  year: number;
  netCents: Cents;
  /** Saldo seit heute einschließlich Überfälligem (plus Kontostand, falls angegeben) */
  cumulativeCents: Cents;
}

export interface LiquidityForecast {
  currency: string;
  overdue: LiquidityBucket;
  weeks: LiquidityWeek[];
  later: LiquidityBucket;
  notSent: LiquidityBucket;
  waitingBrand: LiquidityBucket;
  totalInCents: Cents;
  totalOutCents: Cents;
}

/** Montag der Woche eines Tages. */
export function weekStart(date: IsoDate): IsoDate {
  const d = weekday(date);
  return addDays(date, d === 0 ? -6 : 1 - d);
}

/** Kalenderwoche nach ISO 8601 (Woche mit dem ersten Donnerstag ist KW 1). */
export function isoWeek(date: IsoDate): { year: number; week: number } {
  const thursday = addDays(weekStart(date), 3);
  const year = Number(thursday.slice(0, 4));
  const jan4 = `${year}-01-04`;
  const firstMonday = weekStart(jan4);
  const days = Math.round((Date.parse(`${thursday}T00:00:00Z`) - Date.parse(`${firstMonday}T00:00:00Z`)) / 86400000);
  return { year, week: Math.floor(days / 7) + 1 };
}

const emptyBucket = (): LiquidityBucket => ({ inCents: 0, outCents: 0, items: [] });
const put = (b: LiquidityBucket, i: LiquidityItem) => {
  if (i.kind === "in") b.inCents += i.amountCents;
  else b.outCents += i.amountCents;
  b.items.push(i.id);
};

/**
 * Vorschau je Währung: Überfällig (fällig vor heute), `weeks` Wochen ab der laufenden Woche, danach „später“, ohne
 * Termin getrennt nach Grund. `openingCents` je Währung (optional, z. B. Kontostand heute) fließt in den kumulierten Saldo.
 */
export function liquidityForecast(items: readonly LiquidityItem[], opts: { today: IsoDate; weeks: number; openingCents?: Record<string, Cents> }): LiquidityForecast[] {
  const weeks = Math.max(1, Math.min(26, Math.trunc(opts.weeks)));
  const first = weekStart(opts.today);
  const currencies = [...new Set(items.filter((i) => i.amountCents > 0).map((i) => i.currency))].sort((a, b) => (a === "EUR" ? -1 : b === "EUR" ? 1 : a.localeCompare(b)));
  return currencies.map((currency) => {
    const f: LiquidityForecast = {
      currency, overdue: emptyBucket(), later: emptyBucket(), notSent: emptyBucket(), waitingBrand: emptyBucket(), totalInCents: 0, totalOutCents: 0,
      weeks: Array.from({ length: weeks }, (_, k) => {
        const start = addDays(first, k * 7);
        const w = isoWeek(start);
        return { start, end: addDays(start, 6), week: w.week, year: w.year, inCents: 0, outCents: 0, netCents: 0, cumulativeCents: 0, items: [] };
      }),
    };
    for (const i of items) {
      if (i.currency !== currency || i.amountCents <= 0) continue;
      if (i.kind === "in") f.totalInCents += i.amountCents;
      else f.totalOutCents += i.amountCents;
      if (!i.due) {
        put(i.waiting === "brand_payment" ? f.waitingBrand : f.notSent, i);
        continue;
      }
      if (i.due < opts.today) {
        put(f.overdue, i);
        continue;
      }
      const k = Math.floor(Math.round((Date.parse(`${i.due}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / 86400000) / 7);
      if (k >= weeks) put(f.later, i);
      else put(f.weeks[k]!, i);
    }
    let run = (opts.openingCents?.[currency] ?? 0) + f.overdue.inCents - f.overdue.outCents;
    for (const w of f.weeks) {
      w.netCents = w.inCents - w.outCents;
      run += w.netCents;
      w.cumulativeCents = run;
    }
    return f;
  });
}
